import React, { useEffect, useState, useCallback } from 'react';
import { hostApiClient } from '../../hostApiClient';
import { 
  Box,
  Button,
  Card,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  FormControlLabel,
  Switch,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Delete, Add, DragIndicator, Visibility, VisibilityOff, Phone, Email, Instagram, Facebook, Send, Link, Language, OpenInNew, Warning as WarningIcon, PersonAdd } from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import { ReactSortable } from 'react-sortablejs';
import { DataGrid } from '@mui/x-data-grid';
import { Member } from '../../models/Member';
import {
  AutoConfirmSettings,
  ConfirmAttendanceSettings,
  DiscountSettings,
  Organization,
  OrganizationType,
  PaymentMethod,
} from '../../models/Organization';
import type { BookingSlotsConfig } from '../../models/Room';
import BookingSlotsConfigFields from '../../components/BookingSlotsConfigFields';
import RegulationEditor from '../../components/RegulationEditor';
import {
  DEFAULT_ORG_BOOKING_SLOTS,
  bookingSlotsConfigsEqual,
  cloneBookingSlotsConfig,
} from '../../utils/bookingSlotsConfig';
import { Contact, ContactType } from '../../models/Contact';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { NotificationSnackbar } from '../../components/common/NotificationSnackbar';
import type { SnackbarSeverity } from '../../components/common/NotificationSnackbar';
import NumberInput from '../../components/NumberInput';

import { format } from 'date-fns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PaymentIcon from '@mui/icons-material/Payment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import { HOST_GOOGLE_CALENDAR_HREF, GOOGLE_CALENDAR_UI_ENABLED } from '../../utils/organizationSections';

const DEFAULT_CONFIRM_ATTENDANCE_SETTINGS: ConfirmAttendanceSettings = { enabled: false };

/** Toggle organization members list on My Organization (invite link stays). */
const SHOW_ORGANIZATION_MEMBERS = false;

export const formatDateTime = (dateString?: string | null): string => {
  if (!dateString) return 'N/A';
  try {
    const parsedDate = new Date(dateString);
    if (isNaN(parsedDate.getTime())) return 'N/A';
    return format(parsedDate, 'MMM d, yyyy h:mm a');
  } catch {
    return 'N/A';
  }
};

const OrganizationsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [invitationLink, setInvitationLink] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [name, setName] = useState('');
  const [initialName, setInitialName] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');  
  const [members, setMembers] = useState<Member[]>([]);
  const [markedForDeletion, setMarkedForDeletion] = useState<number[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<SnackbarSeverity>('success');
  const [supportedPaymentMethods, setSupportedPaymentMethods] = useState<PaymentMethod[]>([]);
  const [initialSupportedPaymentMethods, setInitialSupportedPaymentMethods] = useState<PaymentMethod[]>([]);
  const [tutorSupportedPaymentMethods, setTutorSupportedPaymentMethods] = useState<PaymentMethod[]>([]);
  const [initialTutorSupportedPaymentMethods, setInitialTutorSupportedPaymentMethods] = useState<PaymentMethod[]>([]);
  const [autoConfirmSettings, setAutoConfirmSettings] = useState<AutoConfirmSettings>({ cash: false, online: false });
  const [initialAutoConfirmSettings, setInitialAutoConfirmSettings] = useState<AutoConfirmSettings>({ cash: false, online: false });
  const [autoCollectCashPayments, setAutoCollectCashPayments] = useState(false);
  const [initialAutoCollectCashPayments, setInitialAutoCollectCashPayments] = useState(false);
  const [discountSettings, setDiscountSettings] = useState<DiscountSettings>({
    singleBookingDiscount: {
      enabled: false,
      minimumBookingHours: 3,
      discountPercent: 10,
    },
  });
  const [initialDiscountSettings, setInitialDiscountSettings] = useState<DiscountSettings>({
    singleBookingDiscount: {
      enabled: false,
      minimumBookingHours: 3,
      discountPercent: 10,
    },
  });
  const [confirmAttendanceSettings, setConfirmAttendanceSettings] = useState<ConfirmAttendanceSettings>({
    ...DEFAULT_CONFIRM_ATTENDANCE_SETTINGS,
  });
  const [initialConfirmAttendanceSettings, setInitialConfirmAttendanceSettings] =
    useState<ConfirmAttendanceSettings>({ ...DEFAULT_CONFIRM_ATTENDANCE_SETTINGS });
  const [cancellationDeadlineHours, setCancellationDeadlineHours] = useState<number>(8);
  const [initialCancellationDeadlineHours, setInitialCancellationDeadlineHours] = useState<number>(8);
  const [refundPercentageBeforeDeadline, setRefundPercentageBeforeDeadline] = useState<number>(100);
  const [initialRefundPercentageBeforeDeadline, setInitialRefundPercentageBeforeDeadline] = useState<number>(100);
  const [refundPercentageAfterDeadline, setRefundPercentageAfterDeadline] = useState<number>(0);
  const [initialRefundPercentageAfterDeadline, setInitialRefundPercentageAfterDeadline] = useState<number>(0);
  const [contacts, setContacts] = useState<(Contact & { id: string })[]>([]);
  const [initialContacts, setInitialContacts] = useState<(Contact & { id: string })[]>([]);
  const [bookingSlotsConfig, setBookingSlotsConfig] = useState<BookingSlotsConfig>({
    ...DEFAULT_ORG_BOOKING_SLOTS,
  });
  const [initialBookingSlotsConfig, setInitialBookingSlotsConfig] = useState<BookingSlotsConfig | null>(null);
  const [regulation, setRegulation] = useState<string | undefined>(undefined);
  const [initialRegulation, setInitialRegulation] = useState<string | undefined>(undefined);
  const [stripeConnected, setStripeConnected] = useState<boolean | null>(null);
  const navigate = useNavigate();

  const fetchOrganization = useCallback(async () => {
    setLoading(true);

    try {
      const org = await hostApiClient.getOrganization();
      setOrganization(org);
      setName(org?.name ?? '');
      setUpdatedAt(org?.updatedAt ?? '')
      setInitialName(org?.name ?? '');
      setMembers([...org?.members ?? []]);
      setInvitationLink(org.joinCode); // assuming this exists
      // Ensure at least one payment method is selected for room and tutor lists
      const paymentMethods = org?.supportedPaymentMethods ?? [];
      const defaultPaymentMethods = paymentMethods.length > 0 ? paymentMethods : [PaymentMethod.CASH];
      setSupportedPaymentMethods([...defaultPaymentMethods]);
      setInitialSupportedPaymentMethods([...defaultPaymentMethods]);
      const tutorPaymentMethods = org?.tutorSupportedPaymentMethods ?? [];
      const defaultTutorPaymentMethods =
        tutorPaymentMethods.length > 0 ? tutorPaymentMethods : [...defaultPaymentMethods];
      setTutorSupportedPaymentMethods([...defaultTutorPaymentMethods]);
      setInitialTutorSupportedPaymentMethods([...defaultTutorPaymentMethods]);
      const nextAutoConfirmSettings: AutoConfirmSettings = {
        cash: defaultPaymentMethods.includes(PaymentMethod.CASH) ? (org?.autoConfirmSettings?.cash ?? false) : false,
        online: defaultPaymentMethods.includes(PaymentMethod.ONLINE) ? (org?.autoConfirmSettings?.online ?? false) : false,
      };
      setAutoConfirmSettings(nextAutoConfirmSettings);
      setInitialAutoConfirmSettings(nextAutoConfirmSettings);
      const cashAvailable =
        defaultPaymentMethods.includes(PaymentMethod.CASH) ||
        defaultTutorPaymentMethods.includes(PaymentMethod.CASH);
      const nextAutoCollectCashPayments = cashAvailable
        ? (org?.autoCollectCashPayments ?? false)
        : false;
      setAutoCollectCashPayments(nextAutoCollectCashPayments);
      setInitialAutoCollectCashPayments(nextAutoCollectCashPayments);
      const nextDiscountSettings: DiscountSettings = {
        singleBookingDiscount: {
          enabled: org?.discountSettings?.singleBookingDiscount?.enabled ?? false,
          minimumBookingHours: org?.discountSettings?.singleBookingDiscount?.minimumBookingHours ?? 3,
          discountPercent: org?.discountSettings?.singleBookingDiscount?.discountPercent ?? 10,
        },
      };
      setDiscountSettings(nextDiscountSettings);
      setInitialDiscountSettings(nextDiscountSettings);
      const nextConfirmAttendanceSettings: ConfirmAttendanceSettings = {
        enabled: org?.confirmAttendanceSettings?.enabled ?? false,
      };
      setConfirmAttendanceSettings(nextConfirmAttendanceSettings);
      setInitialConfirmAttendanceSettings(nextConfirmAttendanceSettings);
      const nextBookingSlotsConfig = org?.bookingSlotsConfig ?? null;
      setInitialBookingSlotsConfig(nextBookingSlotsConfig);
      setBookingSlotsConfig(
        nextBookingSlotsConfig
          ? cloneBookingSlotsConfig(nextBookingSlotsConfig)
          : { ...DEFAULT_ORG_BOOKING_SLOTS },
      );
      const nextRegulation = org?.regulation?.trim() ? org.regulation : undefined;
      setRegulation(nextRegulation);
      setInitialRegulation(nextRegulation);
      
      // Set cancellation policy values (org columns are NOT NULL; DB default is 8 hours)
      setCancellationDeadlineHours(org?.cancellationDeadlineHours ?? 8);
      setInitialCancellationDeadlineHours(org?.cancellationDeadlineHours ?? 8);
      setRefundPercentageBeforeDeadline(org?.refundPercentageBeforeDeadline ?? 100);
      setInitialRefundPercentageBeforeDeadline(org?.refundPercentageBeforeDeadline ?? 100);
      setRefundPercentageAfterDeadline(org?.refundPercentageAfterDeadline ?? 0);
      setInitialRefundPercentageAfterDeadline(org?.refundPercentageAfterDeadline ?? 0);
      
      // Handle contacts: if not present, prefill with org members' phone numbers
      if (org?.contacts && org.contacts.length > 0) {
        // Add temporary ids for ReactSortable
        const contactsWithIds = org.contacts.map((contact, idx) => ({
          ...contact,
          id: `contact-${idx}-${Date.now()}-${Math.random()}`
        }));
        setContacts(contactsWithIds);
        setInitialContacts(contactsWithIds);
      } else if (org?.members && org.members.length > 0) {
        // Prefill with org members' phone numbers
        const prefilledContacts: (Contact & { id: string })[] = org.members.map((member, idx) => ({
          id: `contact-${idx}-${Date.now()}-${Math.random()}`,
          type: ContactType.PHONE_NUMBER,
          value: member.phoneNumber,
          visible: true,
          comment: member.name
        }));
        setContacts(prefilledContacts);
        setInitialContacts(prefilledContacts);
      } else {
        setContacts([]);
        setInitialContacts([]);
      }
    } catch (err) {
      setError(t('organization.fetchError', 'Failed to fetch organization'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  useEffect(() => {
    if (!organization) return;
    let cancelled = false;
    void hostApiClient
      .getStripeConnectExpressAccount()
      .then((account) => {
        if (!cancelled) setStripeConnected(account.chargesEnabled);
      })
      .catch(() => {
        if (!cancelled) setStripeConnected(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organization]);

  // OAuth callback / legacy deep-link → Integrations Google Calendar page
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam === 'not_all_scopes_provided') {
      navigate(`${HOST_GOOGLE_CALENDAR_HREF}?error=not_all_scopes_provided`, { replace: true });
      return;
    }
    if (location.hash === '#google-calendar') {
      navigate(HOST_GOOGLE_CALENDAR_HREF, { replace: true });
    }
  }, [location.hash, navigate, searchParams]);

  const handleUpdate = () => {
    const now = new Date().toISOString();  
    setUpdatedAt(now);
    
    setHasChanges(true);
  };

  const handleNameChange = (value: string) => {
    setName(value);
    setHasChanges(true);
  };
  
  const handleCancelDeletion = () => {
    setName(initialName);
    setSupportedPaymentMethods([...initialSupportedPaymentMethods]);
    setTutorSupportedPaymentMethods([...initialTutorSupportedPaymentMethods]);
    setAutoConfirmSettings({ ...initialAutoConfirmSettings });
    setAutoCollectCashPayments(initialAutoCollectCashPayments);
    setDiscountSettings({
      singleBookingDiscount: {
        enabled: initialDiscountSettings.singleBookingDiscount?.enabled ?? false,
        minimumBookingHours: initialDiscountSettings.singleBookingDiscount?.minimumBookingHours ?? 3,
        discountPercent: initialDiscountSettings.singleBookingDiscount?.discountPercent ?? 10,
      },
    });
    setConfirmAttendanceSettings({ ...initialConfirmAttendanceSettings });
    setCancellationDeadlineHours(initialCancellationDeadlineHours);
    setRefundPercentageBeforeDeadline(initialRefundPercentageBeforeDeadline);
    setRefundPercentageAfterDeadline(initialRefundPercentageAfterDeadline);
    setContacts([...initialContacts]);
    setBookingSlotsConfig(
      initialBookingSlotsConfig
        ? cloneBookingSlotsConfig(initialBookingSlotsConfig)
        : { ...DEFAULT_ORG_BOOKING_SLOTS },
    );
    setRegulation(initialRegulation);
    setMarkedForDeletion([]);
    setHasChanges(false);
    setSnackbarMessage(t('common.changesCancelled', 'Changes cancelled'));
    setSnackbarSeverity('info');
    setSnackbarOpen(true);
  };
  
  const handleConfirmDeletion = async () => {
    try {
      handleUpdate();
      // Remove temporary ids before sending to API
      const contactsWithoutIds = contacts.map(({ id, ...contact }) => contact);
      const displayedInitialBookingSlots = initialBookingSlotsConfig ?? DEFAULT_ORG_BOOKING_SLOTS;
      const includeBookingSlotsConfig =
        initialBookingSlotsConfig != null ||
        !bookingSlotsConfigsEqual(bookingSlotsConfig, displayedInitialBookingSlots);
      await hostApiClient.updateOrganization({ 
        name, 
        updatedAt,
        supportedPaymentMethods,
        tutorSupportedPaymentMethods,
        autoConfirmSettings: {
          cash: autoConfirmSettings.cash,
          online: autoConfirmSettings.online,
        },
        autoCollectCashPayments,
        discountSettings,
        confirmAttendanceSettings: {
          enabled: confirmAttendanceSettings.enabled,
        },
        cancellationDeadlineHours,
        refundPercentageBeforeDeadline,
        refundPercentageAfterDeadline,
        contacts: contactsWithoutIds,
        regulation: regulation ?? null,
        ...(includeBookingSlotsConfig ? { bookingSlotsConfig } : {}),
      });
      setInitialName(name);
      setInitialSupportedPaymentMethods([...supportedPaymentMethods]);
      setInitialTutorSupportedPaymentMethods([...tutorSupportedPaymentMethods]);
      setInitialAutoConfirmSettings({ ...autoConfirmSettings });
      setInitialAutoCollectCashPayments(autoCollectCashPayments);
      setInitialDiscountSettings({
        singleBookingDiscount: {
          enabled: discountSettings.singleBookingDiscount?.enabled ?? false,
          minimumBookingHours: discountSettings.singleBookingDiscount?.minimumBookingHours ?? 3,
          discountPercent: discountSettings.singleBookingDiscount?.discountPercent ?? 10,
        },
      });
      setInitialConfirmAttendanceSettings({ ...confirmAttendanceSettings });
      setInitialCancellationDeadlineHours(cancellationDeadlineHours);
      setInitialRefundPercentageBeforeDeadline(refundPercentageBeforeDeadline);
      setInitialRefundPercentageAfterDeadline(refundPercentageAfterDeadline);
      setInitialContacts([...contacts]);
      setInitialRegulation(regulation);
      if (includeBookingSlotsConfig) {
        setInitialBookingSlotsConfig(cloneBookingSlotsConfig(bookingSlotsConfig));
      }
      setMembers((prev) => prev.filter((_, i) => !markedForDeletion.includes(i)));
      setMarkedForDeletion([]);
      setHasChanges(false);
      setSnackbarMessage(t('common.changesSaved', 'Changes saved successfully'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage(t('common.saveError', 'Failed to save changes'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleTogglePaymentMethod = (paymentMethod: PaymentMethod) => {
    setSupportedPaymentMethods(prev => {
      const isCurrentlySupported = prev.includes(paymentMethod);
      if (isCurrentlySupported) {
        // Prevent deselecting if this is the last payment method
        if (prev.length === 1) {
          setSnackbarMessage(t('organization.atLeastOnePaymentMethod', 'At least one payment method must be selected'));
          setSnackbarSeverity('info');
          setSnackbarOpen(true);
          return prev; // Keep the current state
        }
        const updatedMethods = prev.filter(method => method !== paymentMethod);
        setAutoConfirmSettings(current => ({
          cash: updatedMethods.includes(PaymentMethod.CASH) ? current.cash : false,
          online: updatedMethods.includes(PaymentMethod.ONLINE) ? current.online : false,
        }));
        if (
          paymentMethod === PaymentMethod.CASH &&
          !tutorSupportedPaymentMethods.includes(PaymentMethod.CASH)
        ) {
          setAutoCollectCashPayments(false);
        }
        return updatedMethods;
      } else {
        return [...prev, paymentMethod];
      }
    });
    setHasChanges(true);
  };

  const handleToggleTutorPaymentMethod = (paymentMethod: PaymentMethod) => {
    setTutorSupportedPaymentMethods(prev => {
      const isCurrentlySupported = prev.includes(paymentMethod);
      if (isCurrentlySupported) {
        if (prev.length === 1) {
          setSnackbarMessage(
            t('organization.atLeastOneTutorPaymentMethod', 'At least one tutor payment method must be selected')
          );
          setSnackbarSeverity('info');
          setSnackbarOpen(true);
          return prev;
        }
        const updatedMethods = prev.filter(method => method !== paymentMethod);
        if (
          paymentMethod === PaymentMethod.CASH &&
          !supportedPaymentMethods.includes(PaymentMethod.CASH)
        ) {
          setAutoCollectCashPayments(false);
        }
        return updatedMethods;
      }
      return [...prev, paymentMethod];
    });
    setHasChanges(true);
  };

  const handleToggleAutoConfirm = (paymentMethod: PaymentMethod) => {
    const isSupported = supportedPaymentMethods.includes(paymentMethod);
    if (!isSupported) {
      return;
    }

    setAutoConfirmSettings(prev => {
      if (paymentMethod === PaymentMethod.CASH) {
        return { ...prev, cash: !prev.cash };
      }
      return { ...prev, online: !prev.online };
    });
    setHasChanges(true);
  };

  const handleToggleAutoCollectCashPayments = () => {
    const roomCash = supportedPaymentMethods.includes(PaymentMethod.CASH);
    const tutorCash = tutorSupportedPaymentMethods.includes(PaymentMethod.CASH);
    const isIndividualTutor = organization?.organizationType === OrganizationType.INDIVIDUAL_TUTOR;
    const isMusicSchool = organization?.organizationType === OrganizationType.MUSIC_SCHOOL;
    const cashAvailable =
      (!isIndividualTutor && roomCash) || ((isMusicSchool || isIndividualTutor) && tutorCash);
    if (!cashAvailable) {
      return;
    }
    setAutoCollectCashPayments((prev) => !prev);
    setHasChanges(true);
  };

  const handleCancellationDeadlineChange = (value: number) => {
    setCancellationDeadlineHours(value);
    setHasChanges(true);
  };

  const handleSingleBookingDiscountEnabledChange = (enabled: boolean) => {
    setDiscountSettings((prev) => ({
      ...prev,
      singleBookingDiscount: {
        enabled,
        minimumBookingHours: prev.singleBookingDiscount?.minimumBookingHours ?? 3,
        discountPercent: prev.singleBookingDiscount?.discountPercent ?? 10,
      },
    }));
    setHasChanges(true);
  };

  const handleConfirmAttendanceEnabledChange = (enabled: boolean) => {
    setConfirmAttendanceSettings({ enabled });
    setHasChanges(true);
  };

  const handleSingleBookingMinimumHoursChange = (minimumBookingHours: number) => {
    setDiscountSettings((prev) => ({
      ...prev,
      singleBookingDiscount: {
        enabled: prev.singleBookingDiscount?.enabled ?? false,
        minimumBookingHours,
        discountPercent: prev.singleBookingDiscount?.discountPercent ?? 10,
      },
    }));
    setHasChanges(true);
  };

  const handleSingleBookingDiscountPercentChange = (discountPercent: number) => {
    setDiscountSettings((prev) => ({
      ...prev,
      singleBookingDiscount: {
        enabled: prev.singleBookingDiscount?.enabled ?? false,
        minimumBookingHours: prev.singleBookingDiscount?.minimumBookingHours ?? 3,
        discountPercent,
      },
    }));
    setHasChanges(true);
  };

  const handleRefundPercentageBeforeDeadlineChange = (value: number) => {
    setRefundPercentageBeforeDeadline(value);
    setHasChanges(true);
  };

  const handleRefundPercentageAfterDeadlineChange = (value: number) => {
    setRefundPercentageAfterDeadline(value);
    setHasChanges(true);
  };

  const handleBookingSlotsChange = (next: BookingSlotsConfig) => {
    setBookingSlotsConfig(next);
    setHasChanges(true);
  };

  const getContactValuePlaceholder = (type: ContactType): string => {
    switch (type) {
      case ContactType.PHONE_NUMBER:
        return t('organization.contactPlaceholderPhone', 'Phone number');
      case ContactType.EMAIL:
        return t('organization.contactPlaceholderEmail', 'Email address');
      case ContactType.INSTAGRAM:
        return t('organization.contactPlaceholderInstagram', '@username');
      case ContactType.FACEBOOK:
        return t('organization.contactPlaceholderFacebook', 'https://facebook.com/...');
      case ContactType.TELEGRAM:
        return t('organization.contactPlaceholderTelegram', '@username');
      case ContactType.LINKTREE:
        return t('organization.contactPlaceholderLinktree', 'https://linktr.ee/...');
      case ContactType.WEBSITE:
        return t('organization.contactPlaceholderWebsite', 'https://example.com');
      default:
        return t('organization.contactPlaceholder', 'Contact value');
    }
  };

  const handleContactTypeChange = (index: number, type: ContactType) => {
    const newContacts = [...contacts];
    newContacts[index] = { ...newContacts[index], type };
    setContacts(newContacts);
    setHasChanges(true);
  };

  const handleContactValueChange = (index: number, value: string) => {
    const newContacts = [...contacts];
    newContacts[index] = { ...newContacts[index], value };
    setContacts(newContacts);
    setHasChanges(true);
  };

  const handleContactVisibleChange = (index: number, visible: boolean) => {
    const newContacts = [...contacts];
    newContacts[index] = { ...newContacts[index], visible };
    setContacts(newContacts);
    setHasChanges(true);
  };

  const handleContactCommentChange = (index: number, comment: string) => {
    const newContacts = [...contacts];
    newContacts[index] = { ...newContacts[index], comment };
    setContacts(newContacts);
    setHasChanges(true);
  };

  const handleAddContact = () => {
    setContacts([...contacts, {
      id: `contact-${Date.now()}-${Math.random()}`,
      type: ContactType.PHONE_NUMBER,
      value: '',
      visible: true,
      comment: ''
    }]);
    setHasChanges(true);
  };

  const handleRemoveContact = (index: number) => {
    const newContacts = contacts.filter((_, i) => i !== index);
    setContacts(newContacts);
    setHasChanges(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  const isIndividualTutorOrg = organization?.organizationType === OrganizationType.INDIVIDUAL_TUTOR;
  const isMusicSchoolOrg = organization?.organizationType === OrganizationType.MUSIC_SCHOOL;
  /** Music schools book rooms and lessons — only they need separate room/tutor payment toggles. */
  const showSplitPaymentMethods = isMusicSchoolOrg;
  const showRoomPaymentMethods = !isIndividualTutorOrg;
  const showTutorPaymentMethods = isMusicSchoolOrg || isIndividualTutorOrg;
  const roomCashEnabled = supportedPaymentMethods.includes(PaymentMethod.CASH);
  const tutorCashEnabled = tutorSupportedPaymentMethods.includes(PaymentMethod.CASH);
  const cashPaymentsAvailable =
    (showRoomPaymentMethods && roomCashEnabled) || (showTutorPaymentMethods && tutorCashEnabled);
  const roomOnlineEnabled = supportedPaymentMethods.includes(PaymentMethod.ONLINE);
  const tutorOnlineEnabled = tutorSupportedPaymentMethods.includes(PaymentMethod.ONLINE);
  const stripeNotConnected = stripeConnected === false;
  const stripeConnectPath = '/host/stripe-connect';

  const stripeConnectBanner = stripeNotConnected ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'background.default',
        borderRadius: 2,
        px: 3,
        py: 2,
        mt: 2,
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <WarningIcon color="warning" />
        <Box>
          <Typography variant="body1" fontWeight="medium">
            {t('organization.stripeConnectNotConnected', 'Stripe not connected')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('organization.stripeConnectNotConnectedHint', 'Set up Stripe to accept online payments')}
          </Typography>
        </Box>
      </Box>
      <Button
        variant="contained"
        size="small"
        endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
        onClick={() => navigate(stripeConnectPath)}
      >
        {t('organization.stripeConnectSetUp', 'Set up Stripe Connect')}
      </Button>
    </Box>
  ) : null;

  return (
      <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 1000, width: '100%', mx: 'auto'}}>
        <Card elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('organization.title', 'My Organization')}
          </Typography>
          <Divider sx={{ mb: { xs: 2, md: 3 } }}/>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            label={t('organization.name', 'Organization Name')}
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            fullWidth
            sx={{ mb: 1 }}
          />

          <Typography variant="h6" gutterBottom>
            {t('organization.contact', 'Contact Information')}
          </Typography>
          <Box sx={{ mb: 3 }}>
            <ReactSortable
              list={contacts}
              setList={(newList) => {
                setContacts((prev) => {
                  const orderChanged = newList.some((item, i) => item.id !== prev[i]?.id);
                  if (orderChanged) setHasChanges(true);
                  return newList;
                });
              }}
              animation={200}
            >
              {contacts.map((contactItem, index) => (
                <Box 
                  key={contactItem.id} 
                  sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    mb: 1,
                    alignItems: 'flex-start',
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.default',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <IconButton
                    sx={{ 
                      cursor: 'grab',
                      mt: 0.5,
                      '&:active': {
                        cursor: 'grabbing'
                      }
                    }}
                    size="small"
                  >
                    <DragIndicator />
                  </IconButton>
                  <FormControl sx={{ minWidth: 180, flexShrink: 0 }}>
                    <InputLabel>{t('organization.contactType', 'Type')}</InputLabel>
                    <Select
                      value={contactItem.type}
                      onChange={(e) => handleContactTypeChange(index, e.target.value as ContactType)}
                      label={t('organization.contactType', 'Type')}
                    >
                      <MenuItem value={ContactType.PHONE_NUMBER}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone fontSize="small" />
                          {t('organization.contactTypePhone', 'Phone')}
                        </Box>
                      </MenuItem>
                      <MenuItem value={ContactType.EMAIL}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Email fontSize="small" />
                          {t('organization.contactTypeEmail', 'Email')}
                        </Box>
                      </MenuItem>
                      <MenuItem value={ContactType.INSTAGRAM}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Instagram fontSize="small" />
                          {t('organization.contactTypeInstagram', 'Instagram')}
                        </Box>
                      </MenuItem>
                      <MenuItem value={ContactType.FACEBOOK}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Facebook fontSize="small" />
                          {t('organization.contactTypeFacebook', 'Facebook')}
                        </Box>
                      </MenuItem>
                      <MenuItem value={ContactType.TELEGRAM}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Send fontSize="small" />
                          {t('organization.contactTypeTelegram', 'Telegram')}
                        </Box>
                      </MenuItem>
                      <MenuItem value={ContactType.LINKTREE}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Link fontSize="small" />
                          {t('organization.contactTypeLinktree', 'Linktree')}
                        </Box>
                      </MenuItem>
                      <MenuItem value={ContactType.WEBSITE}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Language fontSize="small" />
                          {t('organization.contactTypeWebsite', 'Website')}
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label={t('organization.contactValue', 'Value')}
                    value={contactItem.value}
                    onChange={(e) => handleContactValueChange(index, e.target.value)}
                    sx={{ maxWidth: 300, flexShrink: 0 }}
                    placeholder={getContactValuePlaceholder(contactItem.type)}
                  />
                  <TextField
                    label={t('organization.contactComment', 'Comment')}
                    value={contactItem.comment || ''}
                    onChange={(e) => handleContactCommentChange(index, e.target.value)}
                    sx={{ maxWidth: 300, flexShrink: 0 }}
                    placeholder={t('organization.contactCommentPlaceholder', 'Optional comment')}
                  />
                  <Tooltip
                    title={
                      contactItem.visible
                        ? t('organization.contactVisibleTooltip', 'Visible on your public page')
                        : t('organization.contactHiddenTooltip', 'Hidden from your public page')
                    }
                  >
                    <IconButton
                      onClick={() => handleContactVisibleChange(index, !contactItem.visible)}
                      sx={{ mt: 0.5, ml: 'auto', flexShrink: 0 }}
                      color={contactItem.visible ? 'primary' : 'default'}
                      aria-label={
                        contactItem.visible
                          ? t('organization.contactVisibleTooltip', 'Visible on your public page')
                          : t('organization.contactHiddenTooltip', 'Hidden from your public page')
                      }
                    >
                      {contactItem.visible ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    onClick={() => handleRemoveContact(index)}
                    color="error"
                    sx={{ mt: 0.5, flexShrink: 0 }}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              ))}
            </ReactSortable>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button
                startIcon={<Add />}
                onClick={handleAddContact}
                variant="contained"
                size="small"
              >
                {t('organization.addContact', 'Add Contact')}
              </Button>
            </Box>

          </Box>

          {!isIndividualTutorOrg ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  backgroundColor: 'background.default',
                  borderRadius: 2,
                  px: 3,
                  py: 2,
                  mt: 2,
                  mb: 3,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
                  <PersonAdd color="action" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body1" fontWeight="medium">
                      {t('organization.inviteAdmins', 'Invite new Admin')}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ overflowWrap: 'anywhere' }}
                    >
                      {`${window.location.origin}/join/${invitationLink}`}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/join/${invitationLink}`);
                  }}
                >
                  {t('common.copy', 'Copy')}
                </Button>
              </Box>

              {SHOW_ORGANIZATION_MEMBERS ? (
                <>
              <Typography variant="h6" gutterBottom>
                {t('organization.members', 'Members')}
              </Typography>

              {/* Mobile List View */}
              {isMobile ? (
                <>
                  {members.length === 0 ? (
                    <Box sx={{ 
                      mt: 2, 
                      mb: 2,
                      textAlign: 'center',
                      py: 4,
                      bgcolor: 'background.default',
                      borderRadius: 2
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('organization.noMembers', 'No members yet')}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 2, mx: -1 }}>
                      {members.map((member, index) => {
                        return (
                          <Box
                            key={index}
                            sx={{
                              mb: 1,
                              p: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              bgcolor: 'background.paper',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <Box>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontWeight: 700,
                                  fontSize: '1.125rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  letterSpacing: '-0.01em',
                                  color: 'text.primary',
                                  flex: 1,
                                  minWidth: 0,
                                  mb: 1,
                                }}
                              >
                                {member.name}
                              </Typography>
                              
                              <Divider sx={{ mb: 1 }} />
                              
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'text.secondary',
                                  fontSize: '0.875rem',
                                  mb: 0.5,
                                  lineHeight: 1.4,
                                }}
                              >
                                {member.phoneNumber}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'text.secondary',
                                  fontSize: '0.75rem',
                                }}
                              >
                                {t('organization.registeredAt', 'Registered at')}: {formatDateTime(organization?.createdAt) || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </>
              ) : (
                /* Desktop Table View */
                <DataGrid
                  rows={members.map((member, index) => ({
                    id: index,
                    name: member.name,
                    phoneNumber: member.phoneNumber,
                    createdAt: formatDateTime(organization?.createdAt) || 'N/A',
                  }))}
                  columns={[
                    { field: 'name', headerName: t('organization.memberName', 'Name'), flex: 1 },
                    { field: 'phoneNumber', headerName: t('organization.phoneNumber', 'Phone Number'), flex: 1 },
                    { field: 'createdAt', headerName: t('organization.registeredAt', 'Registered at'), flex: 1 },
                  ]}
                  autoHeight
                  hideFooter
                  sx={{
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiDataGrid-row.Mui-selected': {
                      backgroundColor: 'action.selected',
                    },
                  }}
                />
              )}
                </>
              ) : null}
            </>
          ) : null}

          {GOOGLE_CALENDAR_UI_ENABLED ? (
            <>
              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                {t('organization.googleCalendar', 'Google Calendar')}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  backgroundColor: 'background.default',
                  borderRadius: 2,
                  px: 3,
                  py: 2,
                  mb: 3,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CalendarTodayIcon color="action" />
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {t('organization.googleCalendarIntegration', 'Google Calendar Integration')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(
                        'organization.googleCalendarManageHint',
                        'Connect, disconnect, and review calendar sync health under Integrations.',
                      )}
                    </Typography>
                  </Box>
                </Box>
                <Button variant="outlined" onClick={() => navigate(HOST_GOOGLE_CALENDAR_HREF)}>
                  {t('organization.manageGoogleCalendar', 'Manage Google Calendar')}
                </Button>
              </Box>
            </>
          ) : null}

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            {t('organization.supportedPaymentMethods', 'Payment methods')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {showSplitPaymentMethods
              ? t(
                  'organization.supportedPaymentMethodsDescription',
                  'Choose how customers can pay for room bookings and tutor lessons. Auto-confirm applies to room bookings only.'
                )
              : isIndividualTutorOrg
                ? t(
                    'organization.tutorSupportedPaymentMethodsDescription',
                    'Controls how students can pay for tutor lessons.'
                  )
                : t(
                    'organization.roomSupportedPaymentMethodsDescription',
                    'Controls how customers can pay for room bookings. Auto-confirm applies only to room bookings.'
                  )}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                backgroundColor: 'background.default',
                borderRadius: 2,
                px: 3,
                py: 2,
                mb: 2,
              }}
            >
              {showSplitPaymentMethods ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                    <AttachMoneyIcon color="action" />
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {t('organization.cashPayments', 'Cash Payments')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t(
                          'organization.cashPaymentsMergedDescription',
                          'Enable cash separately for rooms and tutor lessons'
                        )}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, pl: 6 }}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {t('organization.paymentMethodsForRooms', 'Rooms')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {roomCashEnabled
                          ? t('organization.roomCashPaymentsEnabled', 'Enabled - Accept cash for room bookings')
                          : t('organization.roomCashPaymentsDisabled', 'Disabled - Cash not accepted for room bookings')}
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={roomCashEnabled}
                          onChange={() => handleTogglePaymentMethod(PaymentMethod.CASH)}
                        />
                      }
                      label=""
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, pl: 6 }}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {t('organization.paymentMethodsForTutors', 'Tutors')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tutorCashEnabled
                          ? t(
                              'organization.tutorCashPaymentsEnabled',
                              'Enabled - Accept cash payments for tutor bookings'
                            )
                          : t(
                              'organization.tutorCashPaymentsDisabled',
                              'Disabled - Cash payments not accepted for tutor bookings'
                            )}
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={tutorCashEnabled}
                          onChange={() => handleToggleTutorPaymentMethod(PaymentMethod.CASH)}
                        />
                      }
                      label=""
                    />
                  </Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AttachMoneyIcon color="action" />
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {t('organization.cashPayments', 'Cash Payments')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {isIndividualTutorOrg
                          ? tutorCashEnabled
                            ? t(
                                'organization.tutorCashPaymentsEnabled',
                                'Enabled - Accept cash payments for tutor bookings'
                              )
                            : t(
                                'organization.tutorCashPaymentsDisabled',
                                'Disabled - Cash payments not accepted for tutor bookings'
                              )
                          : roomCashEnabled
                            ? t('organization.cashPaymentsEnabled', 'Enabled - Accept cash payments from customers')
                            : t('organization.cashPaymentsDisabled', 'Disabled - Cash payments not accepted')}
                      </Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isIndividualTutorOrg ? tutorCashEnabled : roomCashEnabled}
                        onChange={() =>
                          isIndividualTutorOrg
                            ? handleToggleTutorPaymentMethod(PaymentMethod.CASH)
                            : handleTogglePaymentMethod(PaymentMethod.CASH)
                        }
                      />
                    }
                    label=""
                  />
                </Box>
              )}

              {showRoomPaymentMethods ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 1.5,
                    pl: 6,
                    opacity: roomCashEnabled ? 1 : 0.5,
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {t('organization.autoConfirm', 'Auto Confirm')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {!roomCashEnabled
                        ? t('organization.autoConfirmRequiresCash', 'Enable cash payments to use this setting')
                        : autoConfirmSettings.cash
                          ? t('organization.autoConfirmCashEnabled', 'Enabled - Cash bookings are confirmed instantly')
                          : t('organization.autoConfirmCashDisabled', 'Disabled - Cash bookings require host approval')}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoConfirmSettings.cash}
                        onChange={() => handleToggleAutoConfirm(PaymentMethod.CASH)}
                        disabled={!roomCashEnabled}
                      />
                    }
                    label=""
                  />
                </Box>
              ) : null}

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: 1.5,
                  pl: 6,
                  opacity: cashPaymentsAvailable ? 1 : 0.5,
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {t('organization.autoCollectCashPayments', 'Auto Collect Cash Payments')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {!cashPaymentsAvailable
                      ? t(
                          'organization.autoCollectCashPaymentsRequiresCash',
                          'Enable cash payments to use this setting'
                        )
                      : autoCollectCashPayments
                        ? t(
                            'organization.autoCollectCashPaymentsEnabled',
                            'Enabled - Confirmed cash room and tutor bookings are marked paid after they start'
                          )
                        : t(
                            'organization.autoCollectCashPaymentsDisabled',
                            'Disabled - Cash bookings stay unpaid until you mark payment collected'
                          )}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoCollectCashPayments}
                      onChange={handleToggleAutoCollectCashPayments}
                      disabled={!cashPaymentsAvailable}
                    />
                  }
                  label=""
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                backgroundColor: 'background.default',
                borderRadius: 2,
                px: 3,
                py: 2,
                ...(stripeNotConnected ? { opacity: 0.5 } : {}),
              }}
            >
              {showSplitPaymentMethods ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                    <PaymentIcon color="action" />
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {t('organization.onlinePayments', 'Online Payments')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t(
                          'organization.onlinePaymentsMergedDescription',
                          'Enable online payments separately for rooms and tutor lessons. Requires Stripe Connect.'
                        )}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, pl: 6 }}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {t('organization.paymentMethodsForRooms', 'Rooms')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {roomOnlineEnabled
                          ? t('organization.roomOnlinePaymentsEnabled', 'Enabled - Accept online for room bookings')
                          : t(
                              'organization.roomOnlinePaymentsDisabled',
                              'Disabled - Online not accepted for room bookings'
                            )}
                      </Typography>
                    </Box>
                    <Tooltip
                      title={
                        stripeNotConnected
                          ? t(
                              'organization.stripeRequiredTooltip',
                              'Set up Stripe Connect first to enable online payments'
                            )
                          : ''
                      }
                      arrow
                    >
                      <span>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={roomOnlineEnabled}
                              onChange={() => handleTogglePaymentMethod(PaymentMethod.ONLINE)}
                              disabled={stripeNotConnected}
                            />
                          }
                          label=""
                        />
                      </span>
                    </Tooltip>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, pl: 6 }}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {t('organization.paymentMethodsForTutors', 'Tutors')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tutorOnlineEnabled
                          ? t(
                              'organization.tutorOnlinePaymentsEnabled',
                              'Enabled - Accept online payments for tutor bookings'
                            )
                          : t(
                              'organization.tutorOnlinePaymentsDisabled',
                              'Disabled - Online payments not accepted for tutor bookings'
                            )}
                      </Typography>
                    </Box>
                    <Tooltip
                      title={
                        stripeNotConnected
                          ? t(
                              'organization.stripeRequiredTooltip',
                              'Set up Stripe Connect first to enable online payments'
                            )
                          : ''
                      }
                      arrow
                    >
                      <span>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={tutorOnlineEnabled}
                              onChange={() => handleToggleTutorPaymentMethod(PaymentMethod.ONLINE)}
                              disabled={stripeNotConnected}
                            />
                          }
                          label=""
                        />
                      </span>
                    </Tooltip>
                  </Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PaymentIcon color="action" />
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {t('organization.onlinePayments', 'Online Payments')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {isIndividualTutorOrg
                          ? tutorOnlineEnabled
                            ? t(
                                'organization.tutorOnlinePaymentsEnabled',
                                'Enabled - Accept online payments for tutor bookings'
                              )
                            : t(
                                'organization.tutorOnlinePaymentsDisabled',
                                'Disabled - Online payments not accepted for tutor bookings'
                              )
                          : roomOnlineEnabled
                            ? t(
                                'organization.onlinePaymentsEnabled',
                                'Enabled - Accept online payments from customers'
                              )
                            : t('organization.onlinePaymentsDisabled', 'Disabled - Online payments not accepted')}
                      </Typography>
                    </Box>
                  </Box>
                  <Tooltip
                    title={
                      stripeNotConnected
                        ? t(
                            'organization.stripeRequiredTooltip',
                            'Set up Stripe Connect first to enable online payments'
                          )
                        : ''
                    }
                    arrow
                  >
                    <span>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isIndividualTutorOrg ? tutorOnlineEnabled : roomOnlineEnabled}
                            onChange={() =>
                              isIndividualTutorOrg
                                ? handleToggleTutorPaymentMethod(PaymentMethod.ONLINE)
                                : handleTogglePaymentMethod(PaymentMethod.ONLINE)
                            }
                            disabled={stripeNotConnected}
                          />
                        }
                        label=""
                      />
                    </span>
                  </Tooltip>
                </Box>
              )}

              {showRoomPaymentMethods ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 1.5,
                    pl: 6,
                    opacity: roomOnlineEnabled ? 1 : 0.5,
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {t('organization.autoConfirm', 'Auto Confirm')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {!roomOnlineEnabled
                        ? t('organization.autoConfirmRequiresOnline', 'Enable online payments to use this setting')
                        : autoConfirmSettings.online
                          ? t(
                              'organization.autoConfirmOnlineEnabled',
                              'Enabled - Online bookings are confirmed instantly'
                            )
                          : t(
                              'organization.autoConfirmOnlineDisabled',
                              'Disabled - Online bookings require host approval'
                            )}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoConfirmSettings.online}
                        onChange={() => handleToggleAutoConfirm(PaymentMethod.ONLINE)}
                        disabled={!roomOnlineEnabled}
                      />
                    }
                    label=""
                  />
                </Box>
              ) : null}
            </Box>

            {stripeConnectBanner}
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            {t('organization.confirmAttendanceSettings', 'Confirm Attendance')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              'organization.confirmAttendanceSettingsDescription',
              'When enabled, students receive notifications to confirm attendance for upcoming tutor lessons. Separate from experimental features.'
            )}
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: 'background.default',
                borderRadius: 2,
                px: 3,
                py: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <HowToRegIcon color="action" />
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {t(
                      'organization.confirmAttendanceEnabled',
                      'Send attendance confirmation notifications'
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {confirmAttendanceSettings.enabled
                      ? t(
                          'organization.confirmAttendanceEnabledOn',
                          'Enabled - Students get notified to confirm upcoming lesson attendance'
                        )
                      : t(
                          'organization.confirmAttendanceEnabledOff',
                          'Disabled - Students are not notified to confirm attendance'
                        )}
                  </Typography>
                </Box>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={confirmAttendanceSettings.enabled}
                    onChange={(event) => handleConfirmAttendanceEnabledChange(event.target.checked)}
                  />
                }
                label=""
              />
            </Box>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            {t('organization.bookingSlotsConfiguration', 'Booking slots')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              'organization.bookingSlotsDescription',
              'Applies to room bookings and tutor lessons in this organization. Customers can only book start times and durations that match these rules.'
            )}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'background.default',
              borderRadius: 2,
              px: 3,
              py: 2,
              mb: 3,
            }}
          >
            <BookingSlotsConfigFields
              value={bookingSlotsConfig}
              onChange={handleBookingSlotsChange}
              i18nPrefix="organization"
              hideTitle
            />
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            {t('organization.regulation', 'Terms and Conditions (T&C)')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              'organization.regulationHelp',
              'Organization-wide T&C via text or file. Shown on all spaces; users must accept before booking when set.'
            )}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'background.default',
              borderRadius: 2,
              px: 3,
              py: 2,
              mb: 3,
            }}
          >
            <RegulationEditor
              value={regulation}
              onChange={(next) => {
                setRegulation(next);
                setHasChanges(true);
              }}
              hideTitle
            />
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            {t('organization.cancellationPolicy', 'Booking Cancellation Policy')}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('organization.cancellationPolicyDescription', 'Configure your organization\'s cancellation and refund policy. It applies to all bookings, including cash.')}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: 'background.default',
                  borderRadius: 2,
                  px: 3,
                  py: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {t('organization.cancellationDeadline', 'Cancellation Deadline (hours)')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('organization.cancellationDeadlineDescription', 'Hours before booking start time when cancellations are allowed')}
                    </Typography>
                  </Box>
                </Box>
                <NumberInput
                  value={cancellationDeadlineHours}
                  label=""
                  onValueChange={handleCancellationDeadlineChange}
                  min={0}
                  max={168}
                  width={120}
                  size="small"
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: 'background.default',
                  borderRadius: 2,
                  px: 3,
                  py: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {t('organization.refundBeforeDeadline', 'Refund % Before Deadline')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('organization.refundBeforeDeadlineDescription', 'Percentage of booking cost refunded when cancelled before deadline')}
                    </Typography>
                  </Box>
                </Box>
                <NumberInput
                  value={refundPercentageBeforeDeadline}
                  label=""
                  onValueChange={handleRefundPercentageBeforeDeadlineChange}
                  min={0}
                  max={100}
                  width={120}
                  size="small"
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: 'background.default',
                  borderRadius: 2,
                  px: 3,
                  py: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {t('organization.refundAfterDeadline', 'Refund % After Deadline')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('organization.refundAfterDeadlineDescription', 'Percentage of booking cost refunded when cancelled after deadline')}
                    </Typography>
                  </Box>
                </Box>
                <NumberInput
                  value={refundPercentageAfterDeadline}
                  label=""
                  onValueChange={handleRefundPercentageAfterDeadlineChange}
                  min={0}
                  max={100}
                  width={120}
                  size="small"
                />
              </Box>
            </Box>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            {t('organization.discountSettings', 'Discount Settings')}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                backgroundColor: 'background.default',
                borderRadius: 2,
                px: 3,
                py: 2,
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {t('organization.singleBookingDiscount', 'Single Booking Discount')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {discountSettings.singleBookingDiscount?.enabled
                      ? t('organization.singleBookingDiscountEnabled', 'Enabled - discount will apply to eligible bookings')
                      : t('organization.singleBookingDiscountDisabled', 'Disabled - no single booking discount')}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(discountSettings.singleBookingDiscount?.enabled)}
                      onChange={(e) => handleSingleBookingDiscountEnabledChange(e.target.checked)}
                    />
                  }
                  label=""
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, pl: 1 }}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {t('organization.minimumBookingHours', 'Minimum Booking Hours')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('organization.minimumBookingHoursDescription', 'Discount applies when booking duration is at least this many hours')}
                  </Typography>
                </Box>
                <NumberInput
                  value={discountSettings.singleBookingDiscount?.minimumBookingHours ?? 3}
                  label=""
                  onValueChange={handleSingleBookingMinimumHoursChange}
                  min={0}
                  max={24}
                  width={120}
                  size="small"
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, pl: 1 }}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {t('organization.discountPercent', 'Discount Percent')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('organization.discountPercentDescription', 'Percentage deducted from eligible single booking totals')}
                  </Typography>
                </Box>
                <NumberInput
                  value={discountSettings.singleBookingDiscount?.discountPercent ?? 10}
                  label=""
                  onValueChange={handleSingleBookingDiscountPercentChange}
                  min={0}
                  max={100}
                  width={120}
                  size="small"
                />
              </Box>
            </Box>
          </Box>


          <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
            <Button variant="contained" onClick={handleConfirmDeletion} disabled={!hasChanges}>
              {t('common.save', 'Save')}
            </Button>
            <Button variant="outlined" onClick={handleCancelDeletion} disabled={!hasChanges}>
              {t('common.cancel', 'Cancel')}
            </Button>
          </Box>
        </Card>

        <NotificationSnackbar
          open={snackbarOpen}
          message={snackbarMessage}
          severity={snackbarSeverity}
          onClose={handleSnackbarClose}
        />
      </Box>
  );
};

export default OrganizationsPage;